"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Crown, Zap, Lock, CheckCircle2, Rocket, X } from "lucide-react";
import dynamic from "next/dynamic";
import Script from "next/script";
import Link from "next/link";
import { useAuth } from "@/contexts/AuthContext";
const YouTubePlayer = dynamic(() => import("./plyr/YoutubePlayer"), {
  ssr: false,
});

const TELEGRAM_URL = "https://t.me/+qXKJZxaZaaVjMjAx";
const VIP_WHATSAPP_URL =
  "https://wa.me/5515988135320?text=Hola%20Patricia%2C%20complet%C3%A9%20las%204%20clases%20y%20quiero%20activar%20mi%20cuenta%20VIP";

// Componente para player VTurb Legacy que recarrega corretamente na navegação
function VTurbLegacyPlayer({ vturbId, vturbProjectId }) {
  const containerRef = useRef(null);
  const projectId = vturbProjectId || 'c9ad2a89-1d3d-45e5-8628-2a2ae0a0b34d';

  useEffect(() => {
    if (!containerRef.current) return;

    // Limpa o container
    containerRef.current.innerHTML = `
      <div id="vid_${vturbId}" style="position: relative; width: 100%; padding: 56.25% 0 0;">
        <img id="thumb_${vturbId}" src="https://images.converteai.net/${projectId}/players/${vturbId}/thumbnail.jpg" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; display: block;" alt="thumbnail">
        <div id="backdrop_${vturbId}" style="-webkit-backdrop-filter: blur(5px); backdrop-filter: blur(5px); position: absolute; top: 0; height: 100%; width: 100%;"></div>
      </div>
    `;

    // Remove script antigo se existir
    const oldScript = document.getElementById(`scr_${vturbId}`);
    if (oldScript) {
      oldScript.remove();
    }

    // Carrega o script novo
    const script = document.createElement('script');
    script.id = `scr_${vturbId}`;
    script.src = `https://scripts.converteai.net/${projectId}/players/${vturbId}/player.js`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup ao desmontar
      const scriptToRemove = document.getElementById(`scr_${vturbId}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [vturbId, projectId]);

  return <div ref={containerRef} />;
}

const videos = [
  {
    youtubeId: "5r9a3Gw7IBg",
    title: "Cómo Funciona la IA (Empieza Aquí)",
    description: "Clase 01",
    showTelegramButton: true,
  },
  {
    youtubeId: "NZqMwFWBXT4",
    title: "Activa tu Cuenta en 5 Minutos",
    description: "Clase 02",
    showRegisterButton: true,
  },
  {
    youtubeId: "i_wpj470XlE",
    title: "Tu Primera Operación con IA",
    description: "Clase 03",
  },
  {
    youtubeId: "19QGqx547-4",
    title: "Plan $100 → $250,000 en 6 Meses",
    description: "Clase 04",
    isSpecial: true,
    showVIPButton: true,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

export default function VideoCard() {
  const { buyerInfo } = useAuth();
  const [lockedPopup, setLockedPopup] = useState(null); // "telegram" | "vip" | null
  
  // Verificar se o usuário completou o acelerador
  const hasUsedAccelerator = buyerInfo?.accelerator_value > 0;

  const handleLockedAction = (type, event) => {
    if (hasUsedAccelerator) return;
    event.preventDefault();
    setLockedPopup(type);
  };

  return (
    <>
    <motion.div initial="hidden" animate="visible" variants={containerVariants}>
      <div className="flex flex-col gap-4">
        {videos.map((video, index) => {
          // Aulas 3 e 4 (index 2 e 3) são bloqueadas se não usou o acelerador
          const isLocked = (index === 2 || index === 3) && !hasUsedAccelerator;
          
          return (
          <div key={video.vturbId || video.youtubeId || index}>
            {/* Subtle divider between lessons */}
            {index > 0 && (
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                <span className="text-xs text-gray-300 font-medium">•</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
              </div>
            )}
            
            {/* Card com bloqueio condicional */}
            <motion.div
              className={`relative bg-white rounded-[16px] shadow-sm border overflow-hidden transition-all ${
                isLocked 
                  ? 'border-gray-200' 
                  : video.isSpecial 
                    ? 'border-amber-200 shadow-md hover:shadow-lg' 
                    : 'border-blue-200 hover:shadow-lg'
              }`}
              variants={itemVariants}
            >
              
            {/* Special badge for last class */}
            {video.isSpecial && (
              <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 px-4 py-2.5 flex items-center justify-center gap-2">
                <Crown className="w-4 h-4 text-white drop-shadow-md" />
                <span className="text-white text-sm font-semibold drop-shadow-md">Clase Premium</span>
              </div>
            )}

            {/* Video player - com overlay de bloqueio */}
            <div className="relative">
              {/* Overlay de bloqueio sobre o vídeo */}
              {isLocked && (
                <div className="absolute inset-0 z-20 bg-[#0a0f1a]/85 backdrop-blur-[2px] flex flex-col items-center justify-center">
                  <div className="w-14 h-14 bg-[#1a2438] rounded-full flex items-center justify-center shadow-lg border border-white/10 mb-3">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-white text-sm font-medium text-center px-4">
                    Completa la Clase 02 y activa<br />tu Pocket para desbloquear
                  </p>
                </div>
              )}
              
              <div className={isLocked ? 'blur-[2px] pointer-events-none' : ''}>
            {video.vturbId ? (
              <div className="w-full">
                {video.vturbLegacy ? (
                  // Estilo JavaScript antigo (com thumbnail)
                  <VTurbLegacyPlayer 
                    vturbId={video.vturbId} 
                    vturbProjectId={video.vturbProjectId} 
                  />
                ) : (
                  // Estilo smartplayer v4
                  <>
                    <Script 
                      src={`https://scripts.converteai.net/${video.vturbProjectId || 'c9ad2a89-1d3d-45e5-8628-2a2ae0a0b34d'}/players/${video.vturbId}/v4/player.js`}
                      strategy="lazyOnload"
                    />
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `<vturb-smartplayer id="vid-${video.vturbId}" style="display: block; margin: 0 auto; width: 100%;"></vturb-smartplayer>`
                      }}
                    />
                  </>
                )}
              </div>
            ) : (
              <div
                style={{
                  position: "relative",
                  paddingBottom: "56.25%",
                  height: 0,
                }}
              >
                {video.youtubeId ? (
                  <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                    <YouTubePlayer videoId={video.youtubeId} forceRefresh={video.forceRefresh} />
                  </div>
                ) : (
                  <iframe
                    src={video.src}
                    className="absolute top-0 left-0 w-full h-full"
                    frameBorder="0"
                    allow="fullscreen"
                    allowFullScreen
                    referrerPolicy="origin"
                  />
                )}
              </div>
            )}
              </div>
            </div>

            {/* Content - sempre visível, sem blur */}
            <div className="p-4" data-clase={video.description.replace('Clase ', '')}>
              <h2 className={`text-base font-semibold ${
                video.isSpecial ? 'text-amber-600' : 'text-gray-900'
              }`}>
                {video.title}
              </h2>
              <p className="text-gray-500 text-sm">{video.description}</p>

              {/* Telegram Button for video 1 */}
              {video.showTelegramButton && (
                <motion.a
                  className="mt-4 w-full bg-gradient-to-br from-[#0088cc] via-[#0099dd] to-[#00aaee] text-white font-bold py-3 px-5 rounded-2xl flex items-center justify-center gap-3 shadow-[0_8px_30px_rgba(0,136,204,0.4)] transition-all hover:shadow-[0_12px_40px_rgba(0,136,204,0.5)] border border-white/20"
                  href={hasUsedAccelerator ? TELEGRAM_URL : "#"}
                  target={hasUsedAccelerator ? "_blank" : undefined}
                  rel={hasUsedAccelerator ? "noopener noreferrer" : undefined}
                  onClick={(e) => handleLockedAction("telegram", e)}
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  </div>
                  <span className="text-base">Entrar al Grupo de Premios</span>
                </motion.a>
              )}

              {/* CTA Button for video 2 */}
              {video.showRegisterButton && (
                <motion.div
                  className="mt-4"
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    className="w-full bg-gradient-to-br from-purple-400 via-violet-500 to-purple-600 text-white font-bold py-3 px-5 rounded-2xl flex items-center justify-center gap-3 shadow-[0_8px_30px_rgba(147,51,234,0.4)] transition-all hover:shadow-[0_12px_40px_rgba(147,51,234,0.5)] border border-white/20"
                    href="/accelerator"
                  >
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <Zap className="w-5 h-5" />
                    </div>
                    <span className="text-base">Ir al Acelerador</span>
                  </Link>
                </motion.div>
              )}

              {/* VIP Button for video 4 - contact manager */}
              {video.showVIPButton && (
                <motion.a
                  className="mt-4 w-full bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 text-white font-bold py-3 px-5 rounded-2xl flex items-center justify-center gap-3 shadow-[0_8px_30px_rgba(245,158,11,0.4)] transition-all hover:shadow-[0_12px_40px_rgba(245,158,11,0.5)] border border-white/20"
                  href={hasUsedAccelerator ? VIP_WHATSAPP_URL : "#"}
                  target={hasUsedAccelerator ? "_blank" : undefined}
                  rel={hasUsedAccelerator ? "noopener noreferrer" : undefined}
                  onClick={(e) => handleLockedAction("vip", e)}
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center backdrop-blur-sm drop-shadow-lg">
                    <svg className="w-5 h-5 drop-shadow-lg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <span className="text-base font-bold" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Contactar a mi Gerente VIP</span>
                </motion.a>
              )}
            </div>
            </motion.div>
          </div>
          );
        })}
      </div>
    </motion.div>

    {/* Popup: Telegram / VIP bloqueados até usar o acelerador */}
    <AnimatePresence>
      {lockedPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={() => setLockedPopup(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLockedPopup(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
            >
              <X className="w-4 h-4" />
            </button>

            <div
              className={`p-5 text-center ${
                lockedPopup === "telegram"
                  ? "bg-gradient-to-r from-[#0088cc] via-[#0099dd] to-[#00aaee]"
                  : "bg-gradient-to-r from-amber-700 via-amber-600 to-yellow-700"
              }`}
            >
              <div className="flex justify-center mb-2">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                  <Lock
                    className="w-7 h-7 text-white"
                    style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.45))" }}
                  />
                </div>
              </div>
              <h2
                className="text-xl font-bold text-white"
                style={{ textShadow: "0 2px 6px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.35)" }}
              >
                {lockedPopup === "telegram"
                  ? "Grupo bloqueado"
                  : "Contacto VIP bloqueado"}
              </h2>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-gray-700 text-sm text-center leading-relaxed">
                {lockedPopup === "telegram"
                  ? "Para entrar al grupo, necesitas mirar la Clase 02, usar el Acelerador y activar tu cuenta Pocket."
                  : "Para contactar a tu Gerente VIP, necesitas mirar la Clase 02, usar el Acelerador y activar tu cuenta Pocket."}
              </p>

              <div className="bg-gray-50 rounded-2xl p-4 space-y-3 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="text-gray-700 text-sm">Mira la Clase 02</p>
                </div>
                <div className="flex items-center gap-3">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      lockedPopup === "telegram" ? "bg-[#0088cc]/10" : "bg-amber-100"
                    }`}
                  >
                    <Zap
                      className={`w-4 h-4 ${
                        lockedPopup === "telegram" ? "text-[#0088cc]" : "text-amber-600"
                      }`}
                    />
                  </div>
                  <p className="text-gray-700 text-sm">Usa el Acelerador</p>
                </div>
                <div className="flex items-center gap-3">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      lockedPopup === "telegram" ? "bg-[#0099dd]/10" : "bg-yellow-100"
                    }`}
                  >
                    <Rocket
                      className={`w-4 h-4 ${
                        lockedPopup === "telegram" ? "text-[#0099dd]" : "text-amber-500"
                      }`}
                    />
                  </div>
                  <p className="text-gray-700 text-sm">Activa tu cuenta Pocket</p>
                </div>
              </div>

              <Link
                href="/inicio"
                onClick={() => setLockedPopup(null)}
                className={`w-full text-white font-bold py-3 px-5 rounded-2xl flex items-center justify-center gap-2 ${
                  lockedPopup === "telegram"
                    ? "bg-gradient-to-br from-[#0088cc] via-[#0099dd] to-[#00aaee] shadow-[0_8px_30px_rgba(0,136,204,0.4)]"
                    : "bg-gradient-to-br from-amber-700 via-amber-600 to-yellow-700 shadow-[0_8px_30px_rgba(180,83,9,0.45)]"
                }`}
                style={
                  lockedPopup === "vip"
                    ? { textShadow: "0 2px 4px rgba(0,0,0,0.35)" }
                    : undefined
                }
              >
                Ir a las Clases
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}
